import { CloudActions, ProductServiceUrls } from '@portal/portal-core';
import { call, put } from 'redux-saga/effects';
import {
  clientUserAdminAddSharedComputerResult,
  clientUserAdminModifySharedComputerResult,
} from '../actions';
// `../utils` is untyped JS; sendAction resolves as `any`.
import { sendAction } from '../utils';

// Shared-computer payload is a pass-through domain shape assembled by the modals.
// Fields mirror the object destructured by `CloudActions.userAdmin*SharedComputer`.
interface SharedComputerObj {
  label: unknown;
  identifiers: unknown;
  status: unknown;
  startDate: unknown;
  endDate: unknown;
  contactLabel: unknown;
  features: unknown;
}

// Minimal shape of the admin API config passed through to `sendAction`.
interface AdminClientConfig {
  baseApiUrl: string;
  getAuthToken: () => string;
}

interface AddSharedComputerAction {
  type: string;
  payload: {
    costCenterId: string;
    productAccessId: string;
    sharedComputerObj: SharedComputerObj;
  };
}

interface ModifySharedComputerAction {
  type: string;
  payload: {
    costCenterId: string;
    productAccessId: string;
    sharedComputerObj: SharedComputerObj;
    sharedComputerId: string;
  };
}

// Wrapped response returned by `sendAction`.
interface SharedComputerResponse {
  succeeded?: boolean;
  failed?: boolean;
  error?: unknown;
  data?: Record<string, unknown>;
  errorDetail?: unknown;
}

export function* sendAddNewSharedComputer(
  config: AdminClientConfig,
  action: AddSharedComputerAction,
  timeout = 10000,
): Generator<unknown, void, unknown> {
  const url = ProductServiceUrls.productUrl(config.baseApiUrl);

  const serverAction = CloudActions.userAdminAddSharedComputer(
    action.payload.costCenterId,
    action.payload.productAccessId,
    action.payload.sharedComputerObj,
  );

  try {
    const response = (yield call(
      sendAction,
      config,
      serverAction,
      url,
      timeout,
    )) as SharedComputerResponse;
    const open = true;
    const { succeeded, failed, error, data, errorDetail } = response;
    if (failed) {
      const success = false;
      yield put(clientUserAdminAddSharedComputerResult(open, success, data, error, errorDetail));
    } else if (succeeded) {
      const success = true;
      yield put(clientUserAdminAddSharedComputerResult(open, success, data, error));
      // Original call passed only costCenterId; productName stays undefined at runtime.
      yield put(CloudActions.userAdminForceUpdate(action.payload.costCenterId, undefined));
    }
  } catch (error) {
    const open = true;
    const success = false;
    yield put(clientUserAdminAddSharedComputerResult(open, success, {}, error));
  }
}

export function* sendModifySharedComputer(
  config: AdminClientConfig,
  action: ModifySharedComputerAction,
  timeout = 10000,
): Generator<unknown, void, unknown> {
  const url = ProductServiceUrls.productUrl(config.baseApiUrl);
  const serverAction = CloudActions.userAdminUpdateSharedComputer(
    action.payload.costCenterId,
    action.payload.productAccessId,
    action.payload.sharedComputerObj,
    action.payload.sharedComputerId,
  );

  try {
    const response = (yield call(
      sendAction,
      config,
      serverAction,
      url,
      timeout,
    )) as SharedComputerResponse;
    const open = true;
    const { succeeded, failed, error, data, errorDetail } = response;
    if (failed) {
      const success = false;
      yield put(clientUserAdminModifySharedComputerResult(open, success, data, error, errorDetail));
    } else if (succeeded) {
      const success = true;
      yield put(clientUserAdminModifySharedComputerResult(open, success, data, error));
      // Original call passed only costCenterId; productName stays undefined at runtime.
      yield put(CloudActions.userAdminForceUpdate(action.payload.costCenterId, undefined));
    }
  } catch (error) {
    const open = true;
    const success = false;
    yield put(clientUserAdminModifySharedComputerResult(open, success, {}, error));
  }
}
